import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  FileInput,
  Loader,
  Text,
  Group,
  Button,
  Box,
  SegmentedControl,
} from '@mantine/core';
import { IconCamera, IconUpload } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { useMedplum } from '@medplum/react';
import { DiagnosticReport, Patient, Encounter } from '@medplum/fhirtypes';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { DocumentScannerModal } from '@/components/DocumentScanner/DocumentScannerModal';
import {
  PreImplantationFormState,
  PrenatalCheckResultValue,
} from '../utils/medical-history.types';
import {
  createInitialPreImplantationFormState,
  mapReportToFormState,
  isFormValid,
  savePreImplantationCheck,
} from '../utils/pre-implantation.utils';
import {
  mapPrenatalResultToReportResult,
  mapReportResultToPrenatalResult,
  PRENATAL_CHECK_RESULT_OPTIONS,
} from '../utils/prenatal-check.utils';

interface PreImplantationCheckModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  patient: Patient;
  encounter?: Encounter | null;
  report?: DiagnosticReport | null;
}

export const PreImplantationCheckModal: FC<PreImplantationCheckModalProps> = ({
  opened,
  onClose,
  onSuccess,
  patient,
  encounter,
  report,
}) => {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages', 'common']);
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<PreImplantationFormState | null>(null);

  const [formState, setFormState] = useState<PreImplantationFormState>(
    createInitialPreImplantationFormState()
  );
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [scannerModalOpened, setScannerModalOpened] = useState(false);
  const [baselineSet, setBaselineSet] = useState(false);

  // Initialize form state
  useEffect(() => {
    if (opened) {
      const initialState = report
        ? mapReportToFormState(report)
        : createInitialPreImplantationFormState();
      setFormState(initialState);
      setBaselineSet(true);
    } else {
      baselineRef.current = null;
      setBaselineSet(false);
    }
  }, [opened, report]);

  // Capture baseline
  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { ...formState };
      setBaselineSet(false);
    }
  }, [baselineSet, formState, opened]);

  // Validation
  const formIsValid = isFormValid(formState);

  // Change detection
  const hasUnsavedChanges = useMemo(() => {
    if (!baselineRef.current) {
      return false;
    }
    try {
      return !isEqual(formState, baselineRef.current);
    } catch {
      return true;
    }
  }, [formState]);

  const handleFileSelect = (file: File | null) => {
    setUploadingFile(true);
    setFormState((prev) => ({ ...prev, file }));
    setUploadingFile(false);
  };

  const handleScanComplete = (scannedFile: File) => {
    setFormState((prev) => ({ ...prev, file: scannedFile }));
    setScannerModalOpened(false);
  };

  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormState(baselineRef.current);
    } else {
      setFormState(createInitialPreImplantationFormState());
    }
  };

  const handleSave = async () => {
    if (!formIsValid || saving) {
      return;
    }

    setSaving(true);
    try {
      await savePreImplantationCheck({
        medplum,
        patient,
        encounterId: encounter?.id,
        formState,
        existingReport: report || undefined,
      });

      showNotification({
        color: 'green',
        title: t('medicalHistory.preImplantationCheck.saveSuccessTitle', {
          ns: 'patient-pages',
          defaultValue: 'Save successful',
        }),
        message: report
          ? t('medicalHistory.preImplantationCheck.updateSuccessMessage', {
              ns: 'patient-pages',
              defaultValue: 'Pre-implantation check updated.',
            })
          : t('medicalHistory.preImplantationCheck.createSuccessMessage', {
              ns: 'patient-pages',
              defaultValue: 'Pre-implantation check created.',
            }),
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save pre-implantation check:', error);
      showNotification({
        color: 'red',
        title: t('medicalHistory.preImplantationCheck.saveErrorTitle', {
          ns: 'patient-pages',
          defaultValue: 'Save error',
        }),
        message:
          error instanceof Error
            ? error.message
            : t('medicalHistory.preImplantationCheck.saveErrorMessage', {
                ns: 'patient-pages',
                defaultValue: 'An error occurred. Please try again.',
              }),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      title={
        report
          ? t('medicalHistory.preImplantationCheck.editTitle', {
              ns: 'patient-pages',
              defaultValue: 'Edit Pre-implantation Check',
            })
          : t('medicalHistory.preImplantationCheck.createTitle', {
              ns: 'patient-pages',
              defaultValue: 'New Pre-implantation Check',
            })
      }
      size="lg"
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="md">
        <TextInput
          label={t('medicalHistory.preImplantationCheck.typeLabel', {
            ns: 'patient-pages',
            defaultValue: 'Type',
          })}
          placeholder={t(
            'medicalHistory.preImplantationCheck.typePlaceholder',
            {
              ns: 'patient-pages',
              defaultValue: 'Describe the type of check',
            }
          )}
          value={formState.type}
          onChange={(e) =>
            setFormState((prev) => ({
              ...prev,
              type: e.target.value,
            }))
          }
          required
          error={
            !formState.type.trim() && opened
              ? t('medicalHistory.preImplantationCheck.typeRequired', {
                  ns: 'patient-pages',
                  defaultValue: 'Type is required',
                })
              : null
          }
        />

        <DateInputWrapper
          label={t('medicalHistory.preImplantationCheck.dateLabel', {
            ns: 'patient-pages',
            defaultValue: 'Check date',
          })}
          value={formState.date ? new Date(formState.date) : null}
          onChange={(date) =>
            setFormState((prev) => ({
              ...prev,
              date: date ? date.toISOString().split('T')[0] : '',
            }))
          }
          maxDate={new Date()}
        />

        <Box>
          <Text size="sm" fw={500} mb={4}>
            {t('medicalHistory.preImplantationCheck.resultLabel', {
              ns: 'patient-pages',
              defaultValue: 'Result',
            })}
          </Text>
          <SegmentedControl
            fullWidth
            data={PRENATAL_CHECK_RESULT_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
            value={
              mapReportResultToPrenatalResult(formState.result) || undefined
            }
            onChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                result: mapPrenatalResultToReportResult(
                  value as PrenatalCheckResultValue | null
                ),
              }))
            }
          />
        </Box>

        <Textarea
          label={t('medicalHistory.preImplantationCheck.observationsLabel', {
            ns: 'patient-pages',
            defaultValue: 'Observations',
          })}
          placeholder={t(
            'medicalHistory.preImplantationCheck.observationsPlaceholder',
            {
              ns: 'patient-pages',
              defaultValue: 'Describe your observations',
            }
          )}
          value={formState.observations}
          onChange={(e) =>
            setFormState((prev) => ({
              ...prev,
              observations: e.currentTarget.value,
            }))
          }
          minRows={3}
        />

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {t('medicalHistory.preImplantationCheck.fileLabel', {
              ns: 'patient-pages',
              defaultValue: 'Test file (PDF)',
            })}
          </Text>

          <Group gap="sm" grow>
            <Button
              variant="light"
              leftSection={<IconCamera size={18} />}
              onClick={() => setScannerModalOpened(true)}
              disabled={uploadingFile || saving}
            >
              {t('medicalHistory.preImplantationCheck.scanDocument', {
                ns: 'patient-pages',
                defaultValue: 'Scan document',
              })}
            </Button>

            <FileInput
              placeholder={
                report?.presentedForm?.[0]?.title ||
                t('medicalHistory.preImplantationCheck.filePlaceholder', {
                  ns: 'patient-pages',
                  defaultValue: 'Select file',
                })
              }
              accept="application/pdf"
              value={formState.file}
              onChange={handleFileSelect}
              disabled={uploadingFile || saving}
              rightSection={uploadingFile ? <Loader size="sm" /> : null}
              leftSection={<IconUpload size={18} />}
            />
          </Group>

          {formState.file && (
            <Text size="xs" c="dimmed">
              {formState.file.name} (
              {(formState.file.size / 1024 / 1024).toFixed(2)} MB)
            </Text>
          )}
        </Stack>

        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges}
          isLoading={saving || uploadingFile}
          isFormValid={formIsValid}
          saveLabel={t('common:save', { defaultValue: 'Save' })}
        />
      </Stack>

      <DocumentScannerModal
        opened={scannerModalOpened}
        onClose={() => setScannerModalOpened(false)}
        onScanComplete={handleScanComplete}
        preferredCamera="environment"
      />
    </Modal>
  );
};

export default PreImplantationCheckModal;
